import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Printer, MessageCircle, Copy, Zap } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const range = (s) => `${s.min_orders}${s.max_orders == null ? "+" : `–${s.max_orders}`} orders`;

const planLines = (model, slabs, settings) => {
  if (!model) {
    const lines = (slabs || []).filter((s) => s.active !== false).map((s) => `${range(s)}: ₹${s.rate}/order`);
    if (settings?.multi_order_enabled && settings?.default_multi_order_rate != null) lines.push(`Multi orders: ₹${settings.default_multi_order_rate} each`);
    return { name: "Standard Slab Plan", lines };
  }
  let lines = [];
  if (model.type === "Flat Rate") lines = [`₹${model.rate} per order`];
  else if (model.type === "Custom") lines = [`Base: ₹${model.custom?.base_rate ?? 0} per order`];
  else lines = (model.slabs || []).map((s) => `${range(s)}: ₹${s.rate}/order`);
  if (model.multi_order_enabled) {
    const r = model.multi_order_rate ?? settings?.default_multi_order_rate;
    if (r != null) lines.push(`Multi orders: ₹${r} each`);
  }
  return { name: model.name, lines };
};

const message = (dp, plan) =>
  `Welcome to Connect Tym, ${dp.full_name}!\n\nFE ID: ${dp.fe_id}\nFranchise: ${dp.franchise_partner_name || "Connect Tym (Direct)"}\nLocation: ${dp.location || "-"}\n\nYour Earning Plan: ${plan.name}\n${plan.lines.map((l) => `• ${l}`).join("\n")}\n\nRide safe and happy delivering!`;

const waLink = (dp, plan) => {
  const d = (dp.mobile || "").replace(/\D/g, "");
  return `https://wa.me/${d.length === 10 ? `91${d}` : d}?text=${encodeURIComponent(message(dp, plan))}`;
};

const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

const printKit = (dp, plan) => {
  const w = window.open("", "_blank", "width=520,height=700");
  if (!w) { toast.error("Allow pop-ups to print the kit"); return; }
  const row = (l, v, cls = "") => `<div class="r"><div class="l">${l}</div><div class="v ${cls}">${esc(v)}</div></div>`;
  w.document.write(`<html><head><title>Welcome Kit - ${esc(dp.full_name)}</title><style>
    body{font-family:Arial,sans-serif;display:flex;justify-content:center;padding:32px;color:#0f172a}
    .c{width:380px;border:2px solid #059669;border-radius:16px;overflow:hidden}.h{background:#059669;color:#fff;padding:18px 20px}
    .h b{font-size:20px}.h p{margin:4px 0 0;font-size:12px;opacity:.85}.b{padding:20px}.r{margin-bottom:12px}
    .l{font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#64748b}.v{font-size:16px;font-weight:bold;margin-top:2px}
    .id{font-family:monospace;font-size:22px;color:#059669}.p{background:#ecfdf5;border-radius:10px;padding:12px;margin-top:6px}
    .p b{display:block;margin-bottom:6px}.p li{font-size:13px;margin:3px 0}</style></head><body>
    <div class="c"><div class="h"><b>Connect Tym</b><p>Delivery Partner Welcome Kit</p></div><div class="b">
    ${row("Name", dp.full_name)}${row("FE ID", dp.fe_id, "id")}${row("Franchise", dp.franchise_partner_name || "Connect Tym (Direct)")}${row("Location", dp.location || "-")}
    <div class="p"><b>Earning Plan: ${esc(plan.name)}</b><ul>${plan.lines.map((l) => `<li>${esc(l)}</li>`).join("")}</ul></div>
    </div></div><script>window.onload=function(){window.print()}</script></body></html>`);
  w.document.close();
};

export function DPWelcomeKitDialog({ dp, onClose }) {
  const [plan, setPlan] = useState(null);
  useEffect(() => {
    if (!dp) { setPlan(null); return; }
    const settings = api.get("/settings").then((r) => r.data).catch(() => ({}));
    const src = dp.earning_model_id
      ? api.get(`/earning-models/${dp.earning_model_id}`).then((r) => ({ model: r.data })).catch(() => ({}))
      : api.get("/slabs").then((r) => ({ slabs: r.data })).catch(() => ({ slabs: [] }));
    Promise.all([settings, src]).then(([s, x]) => setPlan(planLines(x.model, x.slabs, s)));
  }, [dp]);

  const copy = async () => {
    try { await navigator.clipboard.writeText(message(dp, plan)); toast.success("Welcome message copied"); }
    catch (e) { toast.error("Could not copy"); }
  };

  return (
    <Dialog open={!!dp} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto bg-card sm:max-w-md" data-testid="dp-welcome-kit-dialog">
        <DialogHeader><DialogTitle className="font-head">Delivery Partner Welcome Kit</DialogTitle></DialogHeader>
        {dp && (
          <>
            <div className="overflow-hidden rounded-2xl border-2 border-emerald-600" data-testid="dp-welcome-kit">
              <div className="flex items-center gap-2 bg-emerald-600 px-5 py-4 text-white">
                <Zap className="h-5 w-5" /><div><p className="font-head text-lg font-bold leading-none">Connect Tym</p><p className="text-xs opacity-80">Delivery Partner Welcome Kit</p></div>
              </div>
              <div className="space-y-3 p-5">
                <Field l="Name" v={dp.full_name} />
                <Field l="FE ID" v={<span className="font-mono text-xl text-emerald-500" data-testid="dp-kit-fe-id">{dp.fe_id}</span>} />
                <div className="grid grid-cols-2 gap-3">
                  <Field l="Franchise" v={<span data-testid="dp-kit-franchise">{dp.franchise_partner_name || "Connect Tym (Direct)"}</span>} />
                  <Field l="Location" v={dp.location || "-"} />
                </div>
                <div className="rounded-xl bg-emerald-500/10 p-3" data-testid="dp-kit-plan">
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Earning Plan</p>
                  {!plan ? <p className="text-sm text-muted-foreground">Loading…</p> : (
                    <>
                      <p className="font-semibold" data-testid="dp-kit-plan-name">{plan.name}</p>
                      <ul className="mt-1 space-y-0.5 text-sm">{plan.lines.map((l) => <li key={l}>• {l}</li>)}</ul>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Button variant="outline" disabled={!plan} onClick={() => printKit(dp, plan)} data-testid="dp-kit-print"><Printer className="mr-1 h-4 w-4" /> Print</Button>
              <Button asChild className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="dp-kit-whatsapp">
                <a href={plan ? waLink(dp, plan) : undefined} target="_blank" rel="noreferrer"><MessageCircle className="mr-1 h-4 w-4" /> WhatsApp</a>
              </Button>
              <Button variant="outline" disabled={!plan} onClick={copy} data-testid="dp-kit-copy"><Copy className="mr-1 h-4 w-4" /> Copy</Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ l, v }) => (
  <div><p className="text-[11px] uppercase tracking-wider text-muted-foreground">{l}</p><div className="font-semibold">{v}</div></div>
);
